import React, {useState} from "react";
import {
    Card,
    CardContent,
    CardActions,
    List,
    ListItem,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    Stack,
    Tooltip,
    IconButton,
    Collapse
} from "@mui/material";
import {EntityTableHeaderProps} from "../EntityList";
import {DragDropContext, Droppable, Draggable, DropResult} from "@hello-pangea/dnd";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface TableContentProps {
    isMobile: boolean;
    entities: any[];
    headers: EntityTableHeaderProps[];
    sortKeys: string[];
    sortDirections: ('asc' | 'desc')[];
    setSort: (key: string | null, direction: 'asc' | 'desc' | null) => void;
    indexes?: boolean;
    page?: number;
    rowsPerPage?: number;
    showLoading?: boolean;
    draggable?: boolean;
    onDragEnd: (result: DropResult) => void;
    renderAccordion?: (row: any) => React.ReactNode;
}

const TableContent: React.FC<TableContentProps> = ({
                                                       isMobile,
                                                       entities,
                                                       headers,
                                                       sortKeys,
                                                       sortDirections,
                                                       setSort,
                                                       indexes = false,
                                                       page = 0,
                                                       rowsPerPage = 10,
                                                       showLoading = false,
                                                       draggable = false,
                                                       onDragEnd,
                                                       renderAccordion
                                                   }) => {

    const getNestedValue = (obj: any, path: string): any => {
        return path.split('.').reduce((acc, part) => acc?.[part], obj);
    }

    const [openRows, setOpenRows] = useState<Record<string | number, boolean>>({});

    if (isMobile && draggable) {
        return (
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable-mobile-list">
                    {(provided) => (
                        <List ref={provided.innerRef} {...provided.droppableProps}>
                            {entities.length === 0 ? (
                                !showLoading ? (
                                    <Typography variant="h6" textAlign="center" color="textSecondary"
                                                fontStyle="italic">
                                        Nebyly nalezeny žádné záznamy
                                    </Typography>
                                ) : (
                                    <>
                                        {Array.from({length: 2}).map((_, index) => (
                                            <ListItem key={`first-${index}`}>
                                                <Skeleton variant="text" width="100%"/>
                                            </ListItem>
                                        ))}
                                        <Typography variant="h6" textAlign="center">
                                            Nebyly nalezeny žádné záznamy
                                        </Typography>
                                        {Array.from({length: 2}).map((_, index) => (
                                            <ListItem key={`second-${index}`}>
                                                <Skeleton variant="text" width="100%"/>
                                            </ListItem>
                                        ))}
                                    </>
                                )
                            ) : (
                                entities.map((entity, index) => {
                                    const rowId = entity.id ?? index;
                                    const isOpen = !!openRows[rowId];
                                    const isDragDisabled = !!(entity.isDragDisabled || entity.boat?.disqualified);
                                    return (
                                        <React.Fragment key={rowId}>
                                            <Draggable
                                                key={rowId}
                                                draggableId={String(rowId)}
                                                index={index}
                                                isDragDisabled={isDragDisabled}
                                            >
                                                {(provided, snapshot) => (
                                                    <ListItem
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        disableGutters
                                                        sx={{
                                                            background: "transparent",
                                                            px: 0
                                                        }}
                                                    >
                                                        <Card
                                                            sx={{
                                                                width: 1,
                                                                border: 1,
                                                                borderColor: "divider",
                                                                borderRadius: 2,
                                                                boxShadow: snapshot.isDragging ? 2 : undefined,
                                                                background: snapshot.isDragging ? "#e3f2fd" : "background.paper",
                                                                transition: "box-shadow 0.2s, background 0.2s"
                                                            }}
                                                        >
                                                            <CardContent sx={{paddingBottom: 0, paddingTop: 1}}>
                                                                <Stack direction="row" alignItems="center"
                                                                       spacing={0.5}>
                                                                    <span
                                                                        {...(isDragDisabled ? {} : provided.dragHandleProps)}
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            cursor: isDragDisabled ? "default" : "grab",
                                                                            marginRight: 8,
                                                                            userSelect: "none",
                                                                            marginTop: 30,
                                                                            opacity: isDragDisabled ? 0.3 : 1
                                                                        }}
                                                                    >
                                                                        <DragIndicatorIcon color={isDragDisabled ? "disabled" : "action"}/>
                                                                    </span>
                                                                    <Stack flex={1} spacing={0.5}>
                                                                        {headers
                                                                            .filter((header) => header.key !== "controls")
                                                                            .map((header, idx) => {
                                                                                const value = getNestedValue(entity, header.key);
                                                                                return (
                                                                                    <Typography
                                                                                        key={`${header.key}-${idx}`}
                                                                                        variant="body2">
                                                                                        <strong>{header.label}: </strong>
                                                                                        {header.render ? header.render(value, entity) : value}
                                                                                    </Typography>
                                                                                );
                                                                            })}
                                                                    </Stack>
                                                                    {indexes && (
                                                                        <Typography variant="body2"
                                                                                    sx={{textAlign: "right"}}>
                                                                            <strong>#</strong> {page * rowsPerPage + index + 1}
                                                                        </Typography>
                                                                    )}
                                                                </Stack>
                                                            </CardContent>
                                                            <CardActions
                                                                sx={{justifyContent: "flex-end", paddingTop: 0}}>
                                                                {headers
                                                                    .filter((header) => header.key === "controls")
                                                                    .map((header) => {
                                                                        return header.render ? header.render(entity) : entity[header.key];
                                                                    })
                                                                }
                                                                {renderAccordion && (
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() =>
                                                                            setOpenRows((prev) => ({
                                                                                ...prev,
                                                                                [rowId]: !isOpen
                                                                            }))
                                                                        }
                                                                        aria-label={isOpen ? "Skrýt detail" : "Zobrazit detail"}
                                                                    >
                                                                        {isOpen ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                                                                    </IconButton>
                                                                )}
                                                            </CardActions>
                                                            {renderAccordion && (
                                                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                                    {renderAccordion(entity)}
                                                                </Collapse>
                                                            )}
                                                        </Card>
                                                    </ListItem>
                                                )}
                                            </Draggable>
                                        </React.Fragment>
                                    );
                                })
                            )}
                            {provided.placeholder}
                        </List>
                    )}
                </Droppable>
            </DragDropContext>
        );
    }

    if (isMobile) {
        return <List>
            {entities.length === 0 ? (
                !showLoading ? (
                    <Typography variant="h6" textAlign="center" color="textSecondary" fontStyle="italic">
                        Nebyly nalezeny žádné záznamy
                    </Typography>
                ) : (
                    <>
                        {Array.from({length: 2}).map((_, index) => (
                            <ListItem key={`first-${index}`}>
                                <Skeleton variant="text" width="100%"/>
                            </ListItem>
                        ))}
                        <Typography variant="h6" textAlign="center">
                            Nebyly nalezeny žádné záznamy
                        </Typography>
                        {Array.from({length: 2}).map((_, index) => (
                            <ListItem key={`second-${index}`}>
                                <Skeleton variant="text" width="100%"/>
                            </ListItem>
                        ))}
                    </>
                )
            ) : (
                entities.map((entity, index) => {
                    const rowId = entity.id ?? index;
                    const isOpen = !!openRows[rowId];
                    return (
                        <React.Fragment key={rowId}>
                            <ListItem>
                                <Card sx={{width: 1}}>
                                    <CardContent sx={{paddingBottom: 0}}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                            <Stack spacing={0.5}>
                                                {headers
                                                    .filter((header) => header.key !== "controls")
                                                    .map((header, idx) => {
                                                        const value = getNestedValue(entity, header.key);
                                                        return (
                                                            <Typography key={`${header.key}-${idx}`} variant="body2">
                                                                <strong>{header.label}: </strong>
                                                                {header.render ? header.render(value, entity) : value}
                                                            </Typography>
                                                        );
                                                    })}
                                            </Stack>
                                            {indexes && (
                                                <Typography variant="body2" sx={{textAlign: "right"}}>
                                                    <strong>#</strong> {page * rowsPerPage + index + 1}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </CardContent>
                                    <CardActions sx={{justifyContent: "flex-end", paddingTop: 0}}>
                                        {headers
                                            .filter((header) => header.key === "controls")
                                            .map((header) => {
                                                return header.render ? header.render(entity) : entity[header.key];
                                            })
                                        }
                                        {renderAccordion && (
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setOpenRows((prev) => ({
                                                        ...prev,
                                                        [rowId]: !isOpen
                                                    }))
                                                }
                                                aria-label={isOpen ? "Skrýt detail" : "Zobrazit detail"}
                                            >
                                                {isOpen ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                                            </IconButton>
                                        )}
                                    </CardActions>
                                    {renderAccordion && (
                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                            {renderAccordion(entity)}
                                        </Collapse>
                                    )}
                                </Card>
                            </ListItem>
                        </React.Fragment>
                    );
                })
            )}
        </List>;
    }

    if (draggable) {
        return (
            <DragDropContext onDragEnd={onDragEnd}>
                <Table>
                    <TableHead sx={{backgroundColor: "primary.main"}}>
                        <TableRow>
                            <TableCell sx={{width: 2, background: "primary.main"}}/>
                            {indexes && (
                                <TableCell sx={{color: "white", fontWeight: "bold", cursor: "pointer"}}
                                           onClick={() => setSort(null, null)}>
                                    <Tooltip title="Zrušit řazení">
                                        <Typography variant="button">#</Typography>
                                    </Tooltip>
                                </TableCell>
                            )}
                            {headers.map((header, index) => (
                                <TableCell key={`${header.key}-${index}`} sx={{color: "white", fontWeight: "bold"}}>
                                    {header.sortable ? (
                                        <TableSortLabel
                                            active={sortKeys.includes(header.key)}
                                            direction={sortDirections[sortKeys.indexOf(header.key)]}
                                            sx={{
                                                color: 'white',
                                                '& .MuiTableSortLabel-icon': {color: 'white !important'},
                                                '&.MuiTableSortLabel-root': {color: 'white'},
                                            }}
                                            onClick={() => {
                                                if (!sortKeys.includes(header.key)) {
                                                    setSort(header.key, 'asc');
                                                    return;
                                                }
                                                switch (sortDirections[sortKeys.indexOf(header.key)]) {
                                                    case 'asc':
                                                        setSort(header.key, 'desc');
                                                        break;
                                                    case 'desc':
                                                        setSort(header.key, null);
                                                        break;
                                                }
                                            }}
                                        >
                                            {header.label}{" "}
                                            {sortKeys.includes(header.key) && sortKeys.length > 1 && ("(" + (sortKeys.indexOf(header.key) + 1) + ")")}
                                        </TableSortLabel>
                                    ) : (
                                        header.label
                                    )}
                                </TableCell>
                            ))}
                            {renderAccordion && (
                                <TableCell sx={{width: 40, background: "primary.main"}}/>
                            )}
                        </TableRow>
                    </TableHead>
                    <Droppable droppableId="droppable-table">
                        {(provided) => (
                            <TableBody
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                            >
                                {entities.length === 0 ? (
                                    !showLoading ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={headers.length + (indexes ? 1 : 0) + (renderAccordion ? 1 : 0)}
                                                sx={{textAlign: "center", padding: 3}}>
                                                <Typography variant="h6">Nebyly nalezeny žádné záznamy</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        <>
                                            {Array.from({length: 2}).map((_, index) => (
                                                <TableRow key={`firstRow-${index}`}>
                                                    {Array.from({length: headers.length + (indexes ? 1 : 0) + (renderAccordion ? 1 : 0)}).map((_, index) => (
                                                        <TableCell key={`firstCell-${index}`}>
                                                            <Skeleton variant="text"/>
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell
                                                    colSpan={headers.length + (indexes ? 1 : 0) + (renderAccordion ? 1 : 0)}
                                                    sx={{textAlign: "center", padding: 3}}>
                                                    <Typography variant="h6">Nebyly nalezeny žádné záznamy</Typography>
                                                </TableCell>
                                            </TableRow>
                                            {Array.from({length: 2}).map((_, index) => (
                                                <TableRow key={`lastRow-${index}`}>
                                                    {Array.from({length: headers.length + (indexes ? 1 : 0) + (renderAccordion ? 1 : 0)}).map((_, index) => (
                                                        <TableCell key={`lastCell-${index}`}>
                                                            <Skeleton variant="text"/>
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </>
                                    )
                                ) : (
                                    entities.map((entity, index) => {
                                        const rowId = entity.id ?? index;
                                        const isOpen = !!openRows[rowId];
                                        const isDragDisabled = !!(entity.isDragDisabled || entity.boat?.disqualified);
                                        return (
                                            <React.Fragment key={rowId}>
                                                <Draggable
                                                    key={rowId}
                                                    draggableId={String(rowId)}
                                                    index={index}
                                                    isDragDisabled={isDragDisabled}
                                                >
                                                    {(provided, snapshot) => (
                                                        <TableRow
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            hover
                                                            style={{
                                                                ...provided.draggableProps.style,
                                                                background: snapshot.isDragging ? "#e3f2fd" : undefined,
                                                            }}
                                                            sx={{
                                                                background: isOpen ? "#e3f2fd" : undefined
                                                            }}
                                                        >
                                                            <TableCell sx={{width: 2, padding: 0, cursor: isDragDisabled ? "default" : "grab"}}>
                                                                <span {...(isDragDisabled ? {} : provided.dragHandleProps)}>
                                                                    <DragIndicatorIcon color={isDragDisabled ? "disabled" : "action"}/>
                                                                </span>
                                                            </TableCell>
                                                            {indexes &&
                                                                <TableCell>{page * rowsPerPage + index + 1}</TableCell>}
                                                            {headers.map((header) => {
                                                                const value = getNestedValue(entity, header.key);
                                                                return (
                                                                    <TableCell
                                                                        key={`${header.label}-${header.key}-${index}`}
                                                                        sx={header.key === 'controls' ? {textAlign: 'right'} : {}}
                                                                    >
                                                                        {header.key === 'controls'
                                                                            ? (header.render ? header.render(entity) : null)
                                                                            : (header.render ? header.render(value, entity) : value)}
                                                                    </TableCell>
                                                                );
                                                            })}
                                                            {renderAccordion && (
                                                                <TableCell>
                                                                    <IconButton
                                                                        aria-label={isOpen ? "Skrýt detail" : "Zobrazit detail"}
                                                                        size="small"
                                                                        sx={{p: 0}}
                                                                        onClick={() =>
                                                                            setOpenRows((prev) => ({
                                                                                ...prev,
                                                                                [rowId]: !isOpen
                                                                            }))
                                                                        }
                                                                    >
                                                                        {isOpen ? <KeyboardArrowUpIcon/> :
                                                                            <KeyboardArrowDownIcon/>}
                                                                    </IconButton>
                                                                </TableCell>
                                                            )}
                                                        </TableRow>
                                                    )}
                                                </Draggable>
                                                {renderAccordion && (
                                                    <TableRow>
                                                        <TableCell
                                                            style={{paddingBottom: 0, paddingTop: 0}}
                                                            colSpan={headers.length + (indexes ? 1 : 0) + 2}
                                                        >
                                                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                                {renderAccordion(entity)}
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                                {provided.placeholder}
                            </TableBody>
                        )}
                    </Droppable>
                </Table>
            </DragDropContext>
        );
    }

    return <Table>
        <TableHead sx={{backgroundColor: "primary.main"}}>
            <TableRow>
                {indexes && (
                    <TableCell sx={{color: "white", fontWeight: "bold", cursor: "pointer"}}
                               onClick={() => setSort(null, null)}>
                        <Tooltip title="Zrušit řazení">
                            <Typography variant="button">#</Typography>
                        </Tooltip>
                    </TableCell>
                )}
                {headers.map((header, index) => (
                    <TableCell key={`${header.key}-${index}`} sx={{color: "white", fontWeight: "bold"}}>
                        {header.sortable ? (
                            <TableSortLabel
                                active={sortKeys.includes(header.key)}
                                direction={sortDirections[sortKeys.indexOf(header.key)]}
                                sx={{
                                    color: 'white',
                                    '& .MuiTableSortLabel-icon': {
                                        color: 'white !important',
                                    },
                                    '&.MuiTableSortLabel-root': {
                                        color: 'white',
                                    },
                                }}
                                onClick={() => {
                                    if (!sortKeys.includes(header.key)) {
                                        setSort(header.key, 'asc');
                                        return;
                                    }
                                    switch (sortDirections[sortKeys.indexOf(header.key)]) {
                                        case 'asc':
                                            setSort(header.key, 'desc');
                                            break;
                                        case 'desc':
                                            setSort(header.key, null);
                                            break;
                                    }
                                }}
                            >
                                {header.label}{" "}
                                {sortKeys.includes(header.key) && sortKeys.length > 1 && ("(" + (sortKeys.indexOf(header.key) + 1) + ")")}
                            </TableSortLabel>
                        ) : (
                            header.label
                        )}
                    </TableCell>
                ))}
                {renderAccordion && (
                    <TableCell sx={{width: 2}}/> // accordion icon
                )}
            </TableRow>
        </TableHead>
        <TableBody>
            {entities.length === 0 ? (
                !showLoading ? (
                    <TableRow>
                        <TableCell colSpan={headers.length + (indexes ? 1 : 0) + (renderAccordion ? 1 : 0)}
                                   sx={{textAlign: "center", padding: 3}}>
                            <Typography variant="h6">Nebyly nalezeny žádné záznamy</Typography>
                        </TableCell>
                    </TableRow>
                ) : (
                    <>
                        {Array.from({length: 2}).map((_, index) => (
                            <TableRow key={`firstRow-${index}`}>
                                {Array.from({length: headers.length + (indexes ? 1 : 0) + (renderAccordion ? 1 : 0)}).map((_, index) => (
                                    <TableCell key={`firstCell-${index}`}>
                                        <Skeleton variant="text"/>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell colSpan={headers.length + (indexes ? 1 : 0) + (renderAccordion ? 1 : 0)}
                                       sx={{textAlign: "center", padding: 3}}>
                                <Typography variant="h6">Nebyly nalezeny žádné záznamy</Typography>
                            </TableCell>
                        </TableRow>
                        {Array.from({length: 2}).map((_, index) => (
                            <TableRow key={`lastRow-${index}`}>
                                {Array.from({length: headers.length + (indexes ? 1 : 0) + (renderAccordion ? 1 : 0)}).map((_, index) => (
                                    <TableCell key={`lastCell-${index}`}>
                                        <Skeleton variant="text"/>
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </>
                )
            ) : (
                entities.map((entity, index) => {
                    const rowId = entity.id ?? index;
                    const isOpen = !!openRows[rowId];
                    return (
                        <React.Fragment key={rowId}>
                            <TableRow hover sx={{
                                background: isOpen ? "#e3f2fd" : undefined
                            }}>
                                {indexes && <TableCell>{page * rowsPerPage + index + 1}</TableCell>}
                                {headers.map((header) => {
                                    const value = getNestedValue(entity, header.key);
                                    return (
                                        <TableCell
                                            key={`${header.label}-${header.key}-${index}`}
                                            sx={header.key === 'controls' ? {textAlign: 'right'} : {}}
                                        >
                                            {header.key === 'controls'
                                                ? (header.render ? header.render(entity) : null)
                                                : (header.render ? header.render(value, entity) : value)}
                                        </TableCell>
                                    );
                                })}
                                {renderAccordion && (
                                    <TableCell>
                                        <IconButton
                                            aria-label={isOpen ? "Skrýt detail" : "Zobrazit detail"}
                                            size="small"
                                            sx={{p: 0}}
                                            onClick={() =>
                                                setOpenRows((prev) => ({
                                                    ...prev,
                                                    [rowId]: !isOpen
                                                }))
                                            }
                                        >
                                            {isOpen ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                            {renderAccordion && (
                                <TableRow>
                                    <TableCell
                                        style={{paddingBottom: 0, paddingTop: 0}}
                                        colSpan={headers.length + (indexes ? 1 : 0) + 1}
                                    >
                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                            {renderAccordion(entity)}
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            )}
                        </React.Fragment>
                    );
                })
            )}
        </TableBody>
    </Table>;
};

export default TableContent;